/**
 * EmbarcacionDetailPanel — Contenido tabs del SlideOver de embarcación.
 * Tabs: Resumen · Motores · Documentos · Mantenimientos
 *
 * Se usa desde EmbarcacionesPage al hacer click en "Ver bitácora" de cada bote.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, Stack, Chip, IconButton, Alert,
  TextField, MenuItem, Switch, FormControlLabel, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import useToast from '../common/useToast';
import MantenimientosTable from './shared/MantenimientosTable';
import { API_BASE, getAuthHeaders, getEmpresaId, getUserRole, fmtFecha, fmtMoney } from './shared/format';

const TIPOS_MOTOR = ['2T', '4T', 'electrico', 'diesel'];
const COMBUSTIBLES = ['gasolina', 'diesel', 'electrico'];
const POSICIONES = ['estribor', 'babor', 'central', 'popa', 'proa'];
const ESTADOS_MOTOR = [
  { v: 'operativo',     label: 'Operativo',     color: '#16a34a', bg: '#dcfce7' },
  { v: 'mantenimiento', label: 'Mantenimiento', color: '#f59e0b', bg: '#fef3c7' },
  { v: 'reparacion',    label: 'Reparación',    color: '#dc2626', bg: '#fee2e2' },
  { v: 'baja',          label: 'Dado de baja',  color: '#64748b', bg: '#e2e8f0' },
];

const ESTADOS_VIGENCIA = {
  vigente:    { color: '#16a34a', bg: '#dcfce7', label: 'Vigente' },
  por_vencer: { color: '#f59e0b', bg: '#fef3c7', label: 'Por vencer' },
  vencido:    { color: '#dc2626', bg: '#fee2e2', label: 'Vencido' },
};

export default function EmbarcacionDetailPanel({ embarcacion, mantenimientos, onOpenMantenimiento, onProgramarMant, onRefetchMant }) {
  const [tab, setTab] = useState(0);
  // Acceso completo para todos los roles
  const TABS = [
    { key: 'resumen',         label: 'Resumen',        icon: <DescriptionIcon sx={{ fontSize: 16 }} /> },
    { key: 'motores',         label: 'Motores',        icon: <SettingsIcon    sx={{ fontSize: 16 }} /> },
    { key: 'documentos',      label: 'Documentos',     icon: <DescriptionIcon sx={{ fontSize: 16 }} /> },
    { key: 'mantenimientos',  label: 'Mantenimientos', icon: <BuildIcon       sx={{ fontSize: 16 }} /> },
  ];

  const activeKey = TABS[tab]?.key || 'resumen';

  return (
    <Box>
      <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
        <Typography sx={{ fontSize: 11, color: '#0d9488', fontWeight: 700, textTransform: 'uppercase' }}>
          Embarcación
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{embarcacion.nombre}</Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          {embarcacion.tipo || 'Sin tipo'} · Cap {embarcacion.capacidad_total}
          {embarcacion.modelo && ` · ${embarcacion.modelo}`}
          {embarcacion.año && ` · ${embarcacion.año}`}
        </Typography>
      </Box>

      {TABS.length > 1 && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}>
          {TABS.map(t => (
            <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label}
              sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minHeight: 40 }} />
          ))}
        </Tabs>
      )}

      {activeKey === 'resumen'        && <TabResumen embarcacion={embarcacion} />}
      {activeKey === 'motores'        && <TabMotores embarcacionId={embarcacion.id} />}
      {activeKey === 'documentos'     && <TabDocumentos embarcacionId={embarcacion.id} />}
      {activeKey === 'mantenimientos' && (
        <Box>
          <Button startIcon={<AddIcon />} onClick={onProgramarMant}
            sx={{ textTransform: 'none', mb: 1, color: '#0d9488', fontWeight: 700 }}>
            Programar mantenimiento
          </Button>
          <MantenimientosTable
            rows={mantenimientos.filter(m => m.embarcacion_id === embarcacion.id)}
            loading={false}
            onRowClick={r => onOpenMantenimiento(r.id)}
            showEmbarcacion={false} showProveedor showCosto
            defaultSortBy="fecha_programada" defaultSortDir="desc"
            emptyTitle="Sin mantenimientos"
            emptyMessage="Usa 'Programar mantenimiento' para agendar el primero." />
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------------
// TAB Resumen — vista rápida de especs técnicas, agrupada por dominio
// ----------------------------------------------------------------------------
const ICONOS_AMENIDADES = {
  baño: '🚽', asoleadora: '☀️', techo: '⛱️', escalera_baño: '🪜',
  ducha: '🚿', bar: '🍹', nevera: '🧊', cocina: '🍳', cama: '🛏️', tv: '📺',
};

function TabResumen({ embarcacion: e }) {
  const seccionDimensiones = [
    { label: 'Tipo',           value: e.tipo || '—' },
    { label: 'Modelo',         value: e.modelo || '—' },
    { label: 'Astillero',      value: e.astillero || '—' },
    { label: 'Año',            value: e.año || '—' },
    { label: 'Eslora',         value: e.eslora_pies
                                      ? `${e.eslora_pies} pies${e.eslora_m ? ` (${e.eslora_m} m)` : ''}`
                                      : e.eslora_m ? `${e.eslora_m} m` : '—' },
    { label: 'Manga',          value: e.manga_m  ? `${e.manga_m} m` : '—' },
    { label: 'Calado',         value: e.calado_m ? `${e.calado_m} m` : '—' },
    { label: 'Material casco', value: (e.material_casco || '—').replace('_', ' ') },
    { label: 'Combustible',    value: e.capacidad_combustible_l ? `${e.capacidad_combustible_l} L` : '—' },
    { label: 'Vel. máxima',    value: e.velocidad_max_nudos     ? `${e.velocidad_max_nudos} nudos`     : '—' },
    { label: 'Vel. crucero',   value: e.velocidad_crucero_nudos ? `${e.velocidad_crucero_nudos} nudos` : '—' },
  ];

  const seccionCapacidad = [
    { label: 'Mín. para operar', value: `${e.capacidad_minima || 1} pax` },
    { label: 'Máxima certificada', value: `${e.capacidad_total} pax` },
    { label: 'Época de viento', value: e.capacidad_clima_adverso ? `${e.capacidad_clima_adverso} pax` : '—',
      warn: e.capacidad_clima_adverso && e.capacidad_clima_adverso < e.capacidad_total },
  ];

  const seccionAudio = [
    { label: 'Sistema de audio', value: e.sistema_audio || '—' },
    { label: 'Radio',            value: e.radio_comunicacion || '—' },
    { label: 'GPS',              value: e.tiene_gps ? '✓ Sí' : '✗ No' },
    { label: 'Radio VHF',        value: e.tiene_radio_vhf ? '✓ Sí' : '✗ No' },
  ];

  const seccionSeguridad = [
    { label: 'Chalecos adultos', value: e.chalecos_salvavidas_cantidad != null ? `${e.chalecos_salvavidas_cantidad} unidades` : '—' },
    { label: 'Chalecos niños',   value: e.chalecos_salvavidas_ninos_cantidad != null ? `${e.chalecos_salvavidas_ninos_cantidad} unidades` : '—' },
  ];

  return (
    <Stack spacing={1.5}>
      <Seccion titulo="Capacidad">
        {seccionCapacidad.map(r => (
          <Fila key={r.label} label={r.label} value={r.value} highlight={r.warn} />
        ))}
      </Seccion>

      <Seccion titulo="Dimensiones y técnica">
        {seccionDimensiones.map(r => <Fila key={r.label} label={r.label} value={r.value} />)}
      </Seccion>

      {Array.isArray(e.amenidades) && e.amenidades.length > 0 && (
        <Seccion titulo="Amenidades a bordo">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 0.5 }}>
            {e.amenidades.map(a => (
              <Chip key={a} size="small" label={`${ICONOS_AMENIDADES[a] || '•'} ${a.replace('_', ' ')}`}
                sx={{ bgcolor: '#f0fdfa', color: '#0d9488', fontWeight: 600, fontSize: 11 }} />
            ))}
          </Box>
        </Seccion>
      )}

      <Seccion titulo="Audio y comunicación">
        {seccionAudio.map(r => <Fila key={r.label} label={r.label} value={r.value} />)}
      </Seccion>

      <Seccion titulo="Seguridad">
        {seccionSeguridad.map(r => <Fila key={r.label} label={r.label} value={r.value} />)}
      </Seccion>

      {e.caracteristicas_visuales && (
        <Seccion titulo="Características visuales">
          <Typography sx={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>
            {e.caracteristicas_visuales}
          </Typography>
        </Seccion>
      )}
    </Stack>
  );
}

function Seccion({ titulo, children }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
        {titulo}
      </Typography>
      <Box sx={{ p: 1.25, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        {children}
      </Box>
    </Box>
  );
}

function Fila({ label, value, highlight }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
      <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{label}</Typography>
      <Typography sx={{
        fontSize: 12, fontWeight: highlight ? 800 : 600,
        color: highlight ? '#f59e0b' : '#0f172a', textAlign: 'right',
      }}>{value}</Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------------
// TAB Motores
// ----------------------------------------------------------------------------
function TabMotores({ embarcacionId }) {
  const { toast, ToastContainer } = useToast();
  const [motores, setMotores] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);  // null | { motor?, form }
  const [confirmDel, setConfirmDel] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API_BASE}/turismo/flota/motores?empresa_id=${getEmpresaId()}&embarcacion_id=${embarcacionId}&solo_activos=false`,
      { headers: getAuthHeaders() });
    setMotores(await r.json());
    setLoading(false);
  }, [embarcacionId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    fetch(`${API_BASE}/turismo/flota/marcas-motor`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(setMarcas).catch(() => {});
  }, []);

  const emptyForm = () => ({
    marca: 'Mercury', modelo: '', hp: '', tipo_motor: '4T', combustible: 'gasolina',
    cilindrada_cc: '', numero_serie: '', año: '', posicion: '', fecha_instalacion: '',
    horas_uso: '0', horas_baseline: '0', estado: 'operativo', observaciones: '',
  });

  const guardar = async () => {
    if (!editando) return;
    const f = editando.form;
    if (!f.marca.trim()) { toast.error('Marca requerida'); return; }
    const _num = v => (v === '' || v == null) ? null : Number(v);
    const body = {
      empresa_id: getEmpresaId(),
      embarcacion_id: embarcacionId,
      marca: f.marca,
      modelo: f.modelo || null,
      hp: _num(f.hp),
      tipo_motor: f.tipo_motor,
      combustible: f.combustible,
      cilindrada_cc: _num(f.cilindrada_cc),
      numero_serie: f.numero_serie || null,
      año: _num(f.año),
      posicion: f.posicion || null,
      fecha_instalacion: f.fecha_instalacion || null,
      horas_uso: _num(f.horas_uso) ?? 0,
      horas_baseline: _num(f.horas_baseline) ?? 0,
      estado: f.estado,
      observaciones: f.observaciones || null,
    };
    const url = editando.motor
      ? `${API_BASE}/turismo/flota/motores/${editando.motor.id}`
      : `${API_BASE}/turismo/flota/motores`;
    const r = await fetch(url, {
      method: editando.motor ? 'PUT' : 'POST',
      headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); return; }
    toast.success(editando.motor ? 'Motor actualizado' : 'Motor creado');
    setEditando(null);
    cargar();
  };

  const eliminar = async () => {
    if (!confirmDel) return;
    await fetch(`${API_BASE}/turismo/flota/motores/${confirmDel.id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    setConfirmDel(null);
    toast.success('Motor eliminado');
    cargar();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          {motores.length} motor(es) registrados.
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setEditando({ form: emptyForm() })}
          sx={{ textTransform: 'none', color: '#0d9488', fontWeight: 700 }}>
          Agregar motor
        </Button>
      </Box>

      {motores.length === 0 && !loading && (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          No hay motores registrados para esta embarcación.
        </Alert>
      )}

      <Stack spacing={1}>
        {motores.map(m => {
          const estado = ESTADOS_MOTOR.find(s => s.v === m.estado) || ESTADOS_MOTOR[0];
          return (
            <Box key={m.id} sx={{
              p: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff',
              borderLeft: `4px solid ${estado.color}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                    {m.marca} {m.modelo || ''}
                    {m.hp ? ` · ${m.hp} HP` : ''}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                    {m.posicion ? `${m.posicion} · ` : ''}{m.tipo_motor} · {m.combustible}
                    {m.numero_serie ? ` · S/N: ${m.numero_serie}` : ''}
                  </Typography>
                </Box>
                <Chip size="small" label={estado.label}
                  sx={{ bgcolor: estado.bg, color: estado.color, fontWeight: 700, fontSize: 10, height: 18 }} />
                <IconButton size="small" onClick={() => setEditando({
                  motor: m,
                  form: {
                    marca: m.marca, modelo: m.modelo || '', hp: m.hp != null ? String(m.hp) : '',
                    tipo_motor: m.tipo_motor, combustible: m.combustible,
                    cilindrada_cc: m.cilindrada_cc != null ? String(m.cilindrada_cc) : '',
                    numero_serie: m.numero_serie || '', año: m.año != null ? String(m.año) : '',
                    posicion: m.posicion || '', fecha_instalacion: m.fecha_instalacion || '',
                    horas_uso: String(m.horas_uso || 0), horas_baseline: String(m.horas_baseline || 0),
                    estado: m.estado, observaciones: m.observaciones || '',
                  },
                })}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                <IconButton size="small" onClick={() => setConfirmDel(m)} sx={{ color: '#ef4444' }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, fontSize: 11, color: '#475569' }}>
                <span>Horas: <b>{Number(m.horas_uso || 0).toFixed(1)}h</b></span>
                {m.año && <span>Año: <b>{m.año}</b></span>}
                {m.fecha_instalacion && <span>Instalado: <b>{fmtFecha(m.fecha_instalacion)}</b></span>}
              </Box>
            </Box>
          );
        })}
      </Stack>

      {editando && (
        <SlideOver open onClose={() => setEditando(null)}
          title={editando.motor ? 'Editar motor' : 'Nuevo motor'}
          icon={<SettingsIcon />}
          footer={
            <>
              <Button onClick={() => setEditando(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
              <Button variant="contained" onClick={guardar} disableElevation
                sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
                Guardar
              </Button>
            </>
          }>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
              <TextField size="small" select label="Marca *" value={editando.form.marca}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, marca: e.target.value } })}>
                {marcas.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Modelo" value={editando.form.modelo}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, modelo: e.target.value } })}
                placeholder="Verado 250, F300..." />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
              <TextField size="small" type="number" label="HP" value={editando.form.hp}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, hp: e.target.value } })} />
              <TextField size="small" select label="Tipo" value={editando.form.tipo_motor}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, tipo_motor: e.target.value } })}>
                {TIPOS_MOTOR.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Combustible" value={editando.form.combustible}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, combustible: e.target.value } })}>
                {COMBUSTIBLES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
              <TextField size="small" type="number" label="Cilindrada (cc)" value={editando.form.cilindrada_cc}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, cilindrada_cc: e.target.value } })} />
              <TextField size="small" type="number" label="Año" value={editando.form.año}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, año: e.target.value } })} />
              <TextField size="small" select label="Posición" value={editando.form.posicion}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, posicion: e.target.value } })}>
                <MenuItem value="">—</MenuItem>
                {POSICIONES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>
            <TextField size="small" label="Número de serie" value={editando.form.numero_serie}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, numero_serie: e.target.value } })} />
            <TextField size="small" type="date" label="Fecha de instalación" value={editando.form.fecha_instalacion}
              InputLabelProps={{ shrink: true }}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, fecha_instalacion: e.target.value } })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField size="small" type="number" label="Horas de uso actuales"
                value={editando.form.horas_uso}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, horas_uso: e.target.value } })}
                helperText="Acumulado total" />
              <TextField size="small" type="number" label="Horas baseline (al instalar)"
                value={editando.form.horas_baseline}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, horas_baseline: e.target.value } })}
                helperText="0 si nuevo, X si usado" />
            </Box>
            <TextField size="small" select label="Estado" value={editando.form.estado}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, estado: e.target.value } })}>
              {ESTADOS_MOTOR.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
            </TextField>
            <TextField size="small" multiline minRows={2} label="Observaciones"
              value={editando.form.observaciones}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, observaciones: e.target.value } })} />
          </Stack>
        </SlideOver>
      )}

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={eliminar}
        title="¿Eliminar motor?"
        message={confirmDel ? `${confirmDel.marca} ${confirmDel.modelo || ''}. Los mantenimientos asociados perderán su vínculo (no se borran).` : ''}
        confirmText="Eliminar" severity="danger" />
      <ToastContainer />
    </Box>
  );
}

// ----------------------------------------------------------------------------
// TAB Documentos
// ----------------------------------------------------------------------------
function TabDocumentos({ embarcacionId }) {
  const { toast, ToastContainer } = useToast();
  const [documentos, setDocumentos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API_BASE}/turismo/flota/documentos?empresa_id=${getEmpresaId()}&embarcacion_id=${embarcacionId}&solo_activos=false`,
      { headers: getAuthHeaders() });
    setDocumentos(await r.json());
    setLoading(false);
  }, [embarcacionId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    fetch(`${API_BASE}/turismo/flota/tipos-documento`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(setTipos).catch(() => {});
  }, []);

  const tipoLabel = (v) => tipos.find(t => t.value === v)?.label || v;

  const emptyForm = () => ({
    tipo: 'patente_navegacion', nombre_personalizado: '', numero: '',
    autoridad_emisora: 'DIMAR', fecha_emision: '', fecha_vencimiento: '',
    archivo_url: '', observaciones: '',
  });

  const guardar = async () => {
    if (!editando) return;
    const f = editando.form;
    if (!f.fecha_vencimiento) { toast.error('Fecha de vencimiento requerida'); return; }
    if (f.tipo === 'otro' && !f.nombre_personalizado.trim()) {
      toast.error('Si tipo=Otro, indica el nombre del documento.'); return;
    }
    const body = {
      empresa_id: getEmpresaId(),
      embarcacion_id: embarcacionId,
      tipo: f.tipo,
      nombre_personalizado: f.nombre_personalizado || null,
      numero: f.numero || null,
      autoridad_emisora: f.autoridad_emisora || null,
      fecha_emision: f.fecha_emision || null,
      fecha_vencimiento: f.fecha_vencimiento,
      archivo_url: f.archivo_url || null,
      observaciones: f.observaciones || null,
    };
    const url = editando.doc
      ? `${API_BASE}/turismo/flota/documentos/${editando.doc.id}`
      : `${API_BASE}/turismo/flota/documentos`;
    const r = await fetch(url, {
      method: editando.doc ? 'PUT' : 'POST',
      headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); return; }
    toast.success(editando.doc ? 'Documento actualizado' : 'Documento creado');
    setEditando(null);
    cargar();
  };

  const eliminar = async () => {
    if (!confirmDel) return;
    await fetch(`${API_BASE}/turismo/flota/documentos/${confirmDel.id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    setConfirmDel(null);
    toast.success('Documento eliminado');
    cargar();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          {documentos.length} documento(s) registrados.
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setEditando({ form: emptyForm() })}
          sx={{ textTransform: 'none', color: '#0d9488', fontWeight: 700 }}>
          Agregar documento
        </Button>
      </Box>

      {documentos.length === 0 && !loading && (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          No hay documentos legales registrados.
        </Alert>
      )}

      <Stack spacing={1}>
        {documentos.map(d => {
          const v = ESTADOS_VIGENCIA[d.estado_vigencia] || ESTADOS_VIGENCIA.vigente;
          const dias = d.dias_para_vencer;
          return (
            <Box key={d.id} sx={{
              p: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff',
              borderLeft: `4px solid ${v.color}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                    {d.tipo === 'otro' ? d.nombre_personalizado : tipoLabel(d.tipo)}
                    {d.numero && <span style={{ color: '#64748b', fontWeight: 500 }}> · #{d.numero}</span>}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    {d.autoridad_emisora && `${d.autoridad_emisora} · `}
                    Vence: {fmtFecha(d.fecha_vencimiento)}
                    {dias != null && (
                      <span style={{ color: v.color, fontWeight: 700, marginLeft: 6 }}>
                        ({dias < 0 ? `Vencido hace ${Math.abs(dias)}d` :
                          dias === 0 ? 'Hoy' :
                          `${dias} días`})
                      </span>
                    )}
                  </Typography>
                </Box>
                <Chip size="small" label={v.label} icon={d.estado_vigencia === 'vencido' ? <WarningAmberIcon /> : undefined}
                  sx={{ bgcolor: v.bg, color: v.color, fontWeight: 700, fontSize: 10, height: 18 }} />
                <IconButton size="small" onClick={() => setEditando({
                  doc: d,
                  form: {
                    tipo: d.tipo, nombre_personalizado: d.nombre_personalizado || '',
                    numero: d.numero || '', autoridad_emisora: d.autoridad_emisora || '',
                    fecha_emision: d.fecha_emision || '', fecha_vencimiento: d.fecha_vencimiento || '',
                    archivo_url: d.archivo_url || '', observaciones: d.observaciones || '',
                  },
                })}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                <IconButton size="small" onClick={() => setConfirmDel(d)} sx={{ color: '#ef4444' }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              {d.archivo_url && (
                <Button size="small" href={d.archivo_url} target="_blank"
                  sx={{ textTransform: 'none', fontSize: 11, mt: 0.5, p: 0, color: '#3b82f6' }}>
                  📎 Ver archivo
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>

      {editando && (
        <SlideOver open onClose={() => setEditando(null)}
          title={editando.doc ? 'Editar documento' : 'Nuevo documento legal'}
          icon={<DescriptionIcon />}
          footer={
            <>
              <Button onClick={() => setEditando(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
              <Button variant="contained" onClick={guardar} disableElevation
                sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
                Guardar
              </Button>
            </>
          }>
          <Stack spacing={1.5}>
            <TextField size="small" select label="Tipo *" value={editando.form.tipo}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, tipo: e.target.value } })}>
              {tipos.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            {editando.form.tipo === 'otro' && (
              <TextField size="small" label="Nombre del documento *"
                value={editando.form.nombre_personalizado}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, nombre_personalizado: e.target.value } })} />
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField size="small" label="Número del documento" value={editando.form.numero}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, numero: e.target.value } })} />
              <TextField size="small" label="Autoridad emisora" value={editando.form.autoridad_emisora}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, autoridad_emisora: e.target.value } })}
                placeholder="DIMAR" />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField size="small" type="date" label="Fecha de emisión"
                InputLabelProps={{ shrink: true }} value={editando.form.fecha_emision}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, fecha_emision: e.target.value } })} />
              <TextField size="small" type="date" label="Fecha de vencimiento *"
                InputLabelProps={{ shrink: true }} value={editando.form.fecha_vencimiento}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, fecha_vencimiento: e.target.value } })} />
            </Box>
            <TextField size="small" label="URL del archivo (PDF/foto)" value={editando.form.archivo_url}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, archivo_url: e.target.value } })}
              placeholder="https://..." />
            <TextField size="small" multiline minRows={2} label="Observaciones"
              value={editando.form.observaciones}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, observaciones: e.target.value } })} />
          </Stack>
        </SlideOver>
      )}

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={eliminar}
        title="¿Eliminar documento?"
        message={confirmDel ? `Esta acción no se puede deshacer.` : ''}
        confirmText="Eliminar" severity="danger" />
      <ToastContainer />
    </Box>
  );
}
